import { Link, useRouterState } from "@tanstack/react-router";
import { Calculator } from "lucide-react";
import { useEffect } from "react";
import { useIssuers } from "@modules/billing/api/issuers";
import { useAccountingStore } from "../store";

const TABS = [
  { label: "Plan de cuentas", to: "/accounting/accounts" },
  { label: "Libro diario", to: "/accounting/journal" },
  { label: "Mayor", to: "/accounting/ledger" },
  { label: "Informes", to: "/accounting/reports" },
  { label: "Modelo 303", to: "/accounting/vat" },
  { label: "Configuración", to: "/accounting/config" },
];

interface Props {
  title: string;
  subtitle?: string;
  /** Mostrar selector de ejercicio (no aplica a Plan/Config). */
  showEjercicio?: boolean;
  right?: React.ReactNode;
}

export function AccountingHeader({ title, subtitle, showEjercicio = true, right }: Props) {
  const { data: emisores } = useIssuers();
  const { emisorId, setEmisorId, ejercicio, setEjercicio } = useAccountingStore();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Autoselección del primer emisor disponible
  useEffect(() => {
    if (emisorId === null && emisores && emisores.length > 0) {
      setEmisorId(emisores[0].id);
    }
  }, [emisores, emisorId, setEmisorId]);

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <Calculator size={28} className="text-brand-700" /> {title}
          </h1>
          {subtitle && <p className="text-slate-500 mt-1">{subtitle}</p>}
        </div>

        <div className="flex items-end gap-3">
          {right}
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
              Emisor
            </label>
            <select
              value={emisorId ?? ""}
              onChange={(e) => setEmisorId(e.target.value ? Number(e.target.value) : null)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none"
            >
              {emisores?.map((em) => (
                <option key={em.id} value={em.id}>
                  {em.nombre}
                </option>
              ))}
            </select>
          </div>
          {showEjercicio && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                Ejercicio
              </label>
              <select
                value={ejercicio}
                onChange={(e) => setEjercicio(Number(e.target.value))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 outline-none"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((tab) => {
          const active =
            pathname === tab.to || pathname.startsWith(tab.to + "/");
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={[
                "px-4 py-2.5 text-sm font-bold rounded-t-lg border-b-2 transition -mb-px",
                active
                  ? "border-brand-700 text-brand-800 bg-white"
                  : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50",
              ].join(" ")}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
